import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { GetServerSideProps } from 'next'
import queryString from 'query-string'
import Head from 'next/head'

import { recentDecksURL, imageDirectory } from 'environment'

import { generateDefaultDeckSearchV3 } from 'types/search'
import { DeckListResponse } from 'services/apiTypes/deck.types'

import { RequestService } from 'services/request.service'
import { DeckService } from 'services/deck.service'
import CookieService from 'services/cookie.service'

import DeckSearchFromV3 from 'components/deckSearchPage/DeckSearchFormV3'
import Icon from 'components/elements/Icon'
import PageWrapper from 'components/misc/PageWrapper'
import CardBanner from 'components/misc/CardBanner'
import DeckLink, { gridClassUnlocked } from 'components/misc/DeckLink'
import PhatButton from 'components/formElements/PhatButton'
import PhatInput from 'components/formElements/PhatInput'
import Link from 'components/elements/Link'
import PackageListItem from 'components/deckPage/modals/cardPackages/ListItem'
import InlineAd from 'components/advertisments/InlineAd'

import { getSEOOptimizedInfo } from 'utils/Search'
import { LOG_ERROR_AND_REDIRECT } from 'utils/ErrorString'

import styles from './decks.module.scss'

export const ORDER_BY_COOKIE_KEY = 'decksOrderBy'

type Props = {
  deckResults: DeckListResponse
  header?: string
}

const DeckResultPage = ({ deckResults, header }: Props) => {
  const router = useRouter()

  const { description, title } = getSEOOptimizedInfo(router)
  const [exactPageNumber, setExactPageNumber] = useState('')

  const activeSearchFilters = generateDefaultDeckSearchV3(router.query, router.pathname)
  const contentType = activeSearchFilters.packages ? 'Package' : 'Deck'
  const page = Number(router.query.page || 1) || 1

  return (
    <PageWrapper
      key="deck-results-page"
      title={title || `${contentType} Search - Archidekt`}
      description={
        description ||
        'Search our database of Magic The Gathering decks. Search by name, format, cards, commanders, owners and more!'
      }>
      <Head>
        <meta property="og:type" content="website" />
        <meta name="twitter:label1" content="Total results" />
        <meta name="twitter:data1" content={`${deckResults.count}`} />
      </Head>

      <CardBanner
        src={imageDirectory + 'cm1/47a832ae-0a8d-4710-8341-a1b3e3dbf763_art_crop.jpg'}
        height={'200px'}
        artist
        title={
          <p>
            {header || (
              <>
                <Icon name="search" />
                {contentType} Search
              </>
            )}
          </p>
        }
      />

      <div className={styles.container}>
        {router.query.fromFolderId && (
          <div className={styles.returnToFolderLink}>
            <Link href={`/folders/${router.query.fromFolderId}/`}>
              <PhatButton color="orange" icon="arrow left">
                Return to Folder
              </PhatButton>
            </Link>
          </div>
        )}

        <DeckSearchFromV3
          key='deck-search'
          totalResults={deckResults.count}
          className={styles.formContainer}
          accordionOpenByDefault={!!router.query.fromFolderId}
        />

        <div className={styles.adContainer}>
          <InlineAd id="deck-search-top" banner longBanner leaderboard removeOnNoAd renderVisibleOnly={false} />
        </div>

        {!activeSearchFilters.packages && (
          <div className={`${gridClassUnlocked} ${styles.deckGrid}`}>
            {deckResults.results.map(deck => (
              <DeckLink key={deck.id} deck={deck} />
            ))}
          </div>
        )}

        {activeSearchFilters.packages && (
          <div className={styles.packageGrid}>
            {deckResults.results.map(cardPackage => (
              <PackageListItem key={cardPackage.id} package={cardPackage} className={styles.gridItem} />
            ))}
          </div>
        )}

        {!deckResults.results.length && <div className={styles.noResults}>No results found</div>}

        <div className={styles.pageControls}>
          <div>
            <Link
              tabIndex={page !== 1 ? undefined : -1}
              href={page !== 1 ? buildUrlForPage(activeSearchFilters, page - 1) : '/'}>
              <PhatButton tabIndex={-1} disabled={!router.query.page || page === 1}>
                Previous page
              </PhatButton>
            </Link>

            <Link
              tabIndex={deckResults.next ? undefined : -1}
              href={deckResults.next ? buildUrlForPage(activeSearchFilters, page + 1) : '/'}>
              <PhatButton tabIndex={-1} disabled={!deckResults.next}>
                Next page
              </PhatButton>
            </Link>
          </div>

          <div>
            <PhatInput
              header="Page number"
              placeholder="8"
              value={exactPageNumber}
              onChange={setExactPageNumber}
              className={styles.exactPageNumber}
              type="number"
            />

            <Link href={buildUrlForPage(activeSearchFilters, exactPageNumber)}>
              <PhatButton icon="arrow alternate circle right" />
            </Link>
          </div>
        </div>
      </div>
    </PageWrapper>
  )
}

export default DeckResultPage

export const getServerSideProps: GetServerSideProps<Props> = async context => {
  const cachedOrderBy = CookieService.get(ORDER_BY_COOKIE_KEY, context.req?.cookies)

  try {
    const queryParmas: Record<string, any> = { ...context.query }

    if (queryParmas.orderBy) CookieService.setServerCookie({ [ORDER_BY_COOKIE_KEY]: queryParmas.orderBy }, context)
    if (cachedOrderBy && !queryParmas.orderBy) queryParmas.orderBy = cachedOrderBy

    const query = queryString.stringify(queryParmas)

    const deckResults =
      query.length < 1
        ? await new RequestService(context).get(recentDecksURL, {}, true)
        : await new DeckService(context).listV3({}, `/api/decks/v3/?${query}`)

    return { props: { deckResults } }
  } catch (err) {
    return LOG_ERROR_AND_REDIRECT(err, context)
  }
}

const buildUrlForPage = (existingParams: Record<string, any>, pageNumber: string | number): string => {
  const query = queryString.stringify({ ...existingParams, page: Number(pageNumber) || 1 })

  return `/search/decks/?${query}`
}
